import { useEffect, useState } from "react";
import { Fingerprint } from "lucide-react";
import { api } from "../lib/api";

export function StatusPill({ status }) {
  const map = {
    open: "pill-open",
    under_review: "pill-review",
    investigation: "pill-invest",
    escalated: "pill-escal",
    resolved: "pill-resolved",
    closed: "pill-closed",
  };
  const cls = map[status] || "pill-open";
  return <span className={"pill " + cls}>{status?.replaceAll("_", " ") || "unknown"}</span>;
}

export function PriorityPill({ priority }) {
  const cls = {
    low: "pill-low",
    medium: "pill-medium",
    high: "pill-high",
    critical: "pill-critical",
  }[priority] || "pill-low";
  return <span className={"pill " + cls}>{priority}</span>;
}

export function RiskPill({ level }) {
  const cls = {
    low: "pill-resolved",
    medium: "pill-medium",
    high: "pill-high",
    critical: "pill-critical",
  }[level] || "pill-low";
  return <span className={"pill " + cls}>{level || "n/a"}</span>;
}

export function PolicyStatusPill({ status }) {
  const map = {
    draft: "pill-draft",
    pending_approval: "pill-pending",
    approved: "pill-approved",
    rejected: "pill-rejected",
    archived: "pill-archived",
  };
  return <span className={"pill " + (map[status] || "pill-draft")}>{status?.replaceAll("_", " ") || "draft"}</span>;
}

export function FindingsStatusPill({ status }) {
  const map = {
    pending_approval: "pill-pending",
    approved: "pill-approved",
    rejected: "pill-rejected",
  };
  return <span className={"pill " + (map[status] || "pill-pending")}>{status?.replaceAll("_", " ") || "pending"}</span>;
}

export function DataCard({ label, value, sublabel, testid, accent }) {
  return (
    <div className="data-cell p-5 h-full" data-testid={testid}>
      <div className="eyebrow mb-2">{label}</div>
      <div className={"font-display text-3xl font-semibold " + (accent ? "text-blue-700" : "text-slate-900")}>
        {value}
      </div>
      {sublabel && <div className="text-xs text-slate-500 mt-1 font-mono">{sublabel}</div>}
    </div>
  );
}

export function SectionHeader({ title, subtitle, right, editorial }) {
  return (
    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
      <div>
        <h2 className={"tracking-tight text-slate-900 " + (editorial ? "font-editorial text-4xl" : "font-display text-2xl font-semibold")}>{title}</h2>
        {subtitle && <div className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

export function InlineTabs({ tabs, value, onChange }) {
  return (
    <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
      {tabs.map((t) => (
        <button
          key={t.value}
          data-testid={`tab-${t.value}`}
          onClick={() => onChange(t.value)}
          className={
            "px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors " +
            (value === t.value
              ? "border-slate-900 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-900")
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="border border-dashed border-slate-300 p-12 text-center bg-slate-50/50">
      {Icon && <Icon size={28} strokeWidth={1.25} className="mx-auto text-slate-400 mb-3" />}
      <div className="font-display text-lg font-medium text-slate-900">{title}</div>
      {subtitle && <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{subtitle}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Toggle({ checked, onChange, label, sublabel, testid, danger }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={
        "w-full text-left border p-4 transition-all flex items-start gap-4 " +
        (checked
          ? danger ? "incognito-active" : "bg-slate-900 text-white border-slate-900"
          : "bg-white border-slate-200 hover:border-slate-400")
      }
    >
      <div className={"w-10 h-6 flex items-center " + (checked ? "justify-end" : "justify-start")}>
        <div className={"w-5 h-5 " + (checked ? "bg-white" : "bg-slate-300")}></div>
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold">{label}</div>
        {sublabel && <div className={"text-xs mt-0.5 " + (checked ? "text-slate-300" : "text-slate-500")}>{sublabel}</div>}
      </div>
    </button>
  );
}

export function DisclaimerBanner() {
  // Status: null = still loading, true = user already acknowledged (hide), false = show
  const [acknowledged, setAcknowledged] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/disclaimer/status");
        if (!cancelled) setAcknowledged(!!data?.acknowledged);
      } catch {
        // If the check fails (e.g. offline) we default to showing the banner
        if (!cancelled) setAcknowledged(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const acknowledge = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/disclaimer/acknowledge");
      setAcknowledged(true);
    } catch {
      // best-effort , keep banner up if the audit write fails
    } finally {
      setBusy(false);
    }
  };

  if (acknowledged === null || acknowledged) return null;
  return (
    <div className="border-b border-amber-300 bg-amber-50 px-6 py-2 flex items-center gap-3 justify-between">
      <div className="text-xs text-amber-900 leading-relaxed max-w-5xl">
        <span className="font-semibold uppercase tracking-wider text-[10px] mr-2">Disclaimer</span>
        This platform handles confidential HR data. All PropHR AI outputs are advisory only; final decisions require human review, legal counsel review where applicable, and adherence to your organization&apos;s retention &amp; privacy policies (GDPR, CCPA, EEOC, SOC 2, ISO 27001).
      </div>
      <button
        onClick={acknowledge}
        disabled={busy}
        className="text-xs text-amber-900 font-semibold hover:underline shrink-0 disabled:opacity-60"
        data-testid="dismiss-disclaimer"
      >
        {busy ? "SAVING…" : "ACKNOWLEDGE"}
      </button>
    </div>
  );
}

export function FingerprintChip({ fingerprint, label = "SHA-256" }) {
  if (!fingerprint) return null;
  const short = `${fingerprint.slice(0, 8)}…${fingerprint.slice(-6)}`;
  const copy = () => {
    navigator.clipboard?.writeText(fingerprint);
  };
  return (
    <button onClick={copy} title={`${label}: ${fingerprint} (click to copy)`} className="fingerprint hover:border-slate-400" data-testid="fingerprint-chip">
      <Fingerprint size={10} strokeWidth={2} /> {short}
    </button>
  );
}

export function DateRangePicker({ from, to, onChange, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <input
        type="date"
        value={from || ""}
        onChange={(e) => onChange({ from: e.target.value, to })}
        className="input !py-1.5 !text-xs"
        placeholder="From"
        data-testid={testid ? `${testid}-from` : "date-from"}
      />
      <span className="text-xs text-slate-400">→</span>
      <input
        type="date"
        value={to || ""}
        onChange={(e) => onChange({ from, to: e.target.value })}
        className="input !py-1.5 !text-xs"
        placeholder="To"
        data-testid={testid ? `${testid}-to` : "date-to"}
      />
      {(from || to) && (
        <button
          onClick={() => onChange({ from: "", to: "" })}
          className="text-[11px] text-slate-500 hover:text-slate-900 underline"
          data-testid={testid ? `${testid}-clear` : "date-clear"}
        >clear</button>
      )}
    </div>
  );
}
